import React, { useState, useEffect, useRef } from 'react';

const PharmacyView = ({ currentUser, pharmacyStock = [], stockTransactions = [], onRecordTransaction, onAddItem, labOrders = [], pharmacyOrders = [], onProcessLabOrderForPharmacy }) => {
    const [transaction, setTransaction] = useState({ itemId: '', type: 'sortie', quantity: 1, notes: '', signature: '' });
    const [newItem, setNewItem] = useState({ name: '', description: '', quantity: 0, capacity: 100 });
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]); // { itemId, name, qty }
    const [processingOrder, setProcessingOrder] = useState(null);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const searchRef = useRef();

    const handleTransactionChange = (field, value) => setTransaction(prev => ({ ...prev, [field]: value }));
    const handleNewItemChange = (field, value) => setNewItem(prev => ({ ...prev, [field]: value }));

    const submitTransaction = (e) => {
        e.preventDefault();
        if (!transaction.itemId) {
            alert('Veuillez sélectionner un article.');
            return;
        }
        if (!transaction.signature) {
            alert('La signature ou l’empreinte est requise pour valider cette transaction.');
            return;
        }
        onRecordTransaction({
            ...transaction,
            quantity: Number(transaction.quantity),
            performedBy: currentUser?.name || 'Inconnu'
        });
        setTransaction({ itemId: '', type: 'sortie', quantity: 1, notes: '', signature: '' });
    };

    const submitNewItem = (e) => {
        e.preventDefault();
        if (!newItem.name || newItem.quantity < 0 || newItem.capacity <= 0) {
            alert('Veuillez renseigner un article valide pour l’ajout.');
            return;
        }
        onAddItem({ ...newItem, quantity: Number(newItem.quantity), capacity: Number(newItem.capacity) });
        setNewItem({ name: '', description: '', quantity: 0, capacity: 100 });
    };

    const defaultTemplates = [
        {
            id: 'kit-urgent',
            name: 'Kit urgence',
            items: [
                { itemId: 'med-1', name: 'Paracétamol 500mg', qty: 2 },
                { itemId: 'mat-1', name: 'Seringues 5ml', qty: 5 }
            ]
        },
        {
            id: 'kit-antibiotique',
            name: 'Kit antibiotiques',
            items: [
                { itemId: 'med-2', name: 'Amoxicilline 500mg', qty: 2 }
            ]
        }
    ];

    const onSearchChange = (v) => {
        setSearchTerm(v);
        setSelectedSuggestionIndex(-1);
        const q = v.trim().toLowerCase();
        if (!q) return setSuggestions([]);
        const s = pharmacyStock.filter(i => i.name.toLowerCase().includes(q)).slice(0, 8);
        setSuggestions(s);
    };

    useEffect(() => {
        try {
            const storedTemplates = JSON.parse(localStorage.getItem('pharmacyTemplates')) || [];
            const initialTemplates = storedTemplates.length ? storedTemplates : defaultTemplates;
            setTemplates(initialTemplates);
            if (!storedTemplates.length) {
                localStorage.setItem('pharmacyTemplates', JSON.stringify(initialTemplates));
            }
        } catch (e) {
            setTemplates(defaultTemplates);
        }
    }, []);

    const saveTemplatesToStorage = (t) => {
        setTemplates(t);
        localStorage.setItem('pharmacyTemplates', JSON.stringify(t));
    };

    const createTemplate = (name) => {
        if (!name || !selectedItems.length) return alert('Donnez un nom et sélectionnez des articles.');
        const tpl = { id: Date.now(), name, items: selectedItems };
        const updated = [tpl, ...templates];
        saveTemplatesToStorage(updated);
        alert('Template créé');
    };

    const applyTemplate = (id) => {
        const tpl = templates.find(t => t.id === id);
        if (!tpl) return;
        setSelectedItems(tpl.items.map(it => ({ ...it })));
    };

    const importTemplates = () => {
        const json = prompt('Collez le JSON des templates ici :');
        if (!json) return;
        try {
            const imported = JSON.parse(json);
            if (!Array.isArray(imported)) throw new Error('Format invalide');
            const merged = [...templates, ...imported.filter(t => t && t.id && t.name && Array.isArray(t.items))];
            saveTemplatesToStorage(merged);
            alert('Templates importés avec succès.');
        } catch (e) {
            alert('Échec de l’import : ' + e.message);
        }
    };

    const exportTemplates = () => {
        const data = JSON.stringify(templates, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'pharmacy_templates.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const deleteTemplate = (id) => {
        const updated = templates.filter(t => t.id !== id);
        saveTemplatesToStorage(updated);
    };

    const onSearchKeyDown = (e) => {
        if (!suggestions.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedSuggestionIndex(idx => Math.min(idx + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedSuggestionIndex(idx => Math.max(idx - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const idx = selectedSuggestionIndex >= 0 ? selectedSuggestionIndex : 0;
            const s = suggestions[idx];
            if (s) addSelectedItem(s);
        } else if (e.key === 'Escape') {
            setSuggestions([]);
            setSelectedSuggestionIndex(-1);
        }
    };

    const addSelectedItem = (item) => {
        if (selectedItems.find(si => si.itemId === item.id)) return;
        setSelectedItems([...selectedItems, { itemId: item.id, name: item.name, qty: 1 }]);
        setSearchTerm('');
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
        searchRef.current?.focus();
    };

    const updateSelectedQty = (itemId, qty) => {
        setSelectedItems(selectedItems.map(s => s.itemId === itemId ? { ...s, qty: Number(qty) } : s));
    };

    const removeSelected = (itemId) => setSelectedItems(selectedItems.filter(s => s.itemId !== itemId));

    const reserveForOrder = (orderId) => {
        if (!selectedItems.length) {
            alert('Sélectionnez au moins un article à réserver.');
            return;
        }
        const reservations = selectedItems.map(s => ({ itemId: s.itemId, qty: Number(s.qty || 0) }));
        // validate against current stock
        const insufficient = reservations.filter(r => {
            const stockItem = pharmacyStock.find(p => p.id === r.itemId) || { quantity: 0 };
            return r.qty <= 0 || r.qty > (stockItem.quantity || 0);
        });
        if (insufficient.length) {
            return alert('Quantité invalide ou insuffisante pour au moins un article.');
        }
        if (typeof onProcessLabOrderForPharmacy === 'function') {
            onProcessLabOrderForPharmacy(orderId, reservations);
            setSelectedItems([]);
            setProcessingOrder(null);
            alert('Réservation envoyée.');
        } else {
            alert('Handler de traitement labo non disponible.');
        }
    };

    const sortedStock = [...pharmacyStock].sort((a, b) => a.name.localeCompare(b.name));
    const warnings = sortedStock.map(item => {
        const ratio = item.capacity ? item.quantity / item.capacity : 0;
        let severity = '';
        if (ratio >= 0.7) severity = 'Alerte forte : stock élevé';
        else if (ratio <= 0.3) severity = 'Attention : stock faible';
        return { ...item, ratio, severity };
    });

    return (
        <div style={{ padding: '24px' }}>
            <h2>Gestion Pharmacie et Stock</h2>
            <p>Utilisateur : <strong>{currentUser?.name}</strong> ({currentUser?.role})</p>

            <section style={{ marginBottom: '24px', padding: '20px', borderRadius: '16px', backgroundColor: '#f8f9fb', border: '1px solid #dde4ed' }}>
                <h3>Inventaire des médicaments et matériel</h3>
                <div style={{ display: 'grid', gap: '14px' }}>
                    {warnings.map(item => (
                        <div key={item.id} style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d1dce8', backgroundColor: item.ratio >= 0.7 ? '#fff7f0' : item.ratio <= 0.3 ? '#f4f9ff' : '#ffffff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                <strong>{item.name}</strong>
                                <span>{item.quantity} / {item.capacity} unités</span>
                                <span>{Math.round(item.ratio * 100)}%</span>
                            </div>
                            <div style={{ marginTop: '6px', color: item.ratio >= 0.7 ? '#b45309' : item.ratio <= 0.3 ? '#0f766e' : '#334e68' }}>
                                {item.severity || 'Stock normal'}
                            </div>
                            <div style={{ marginTop: '8px', color: '#334e68' }}>{item.description}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section style={{ marginBottom: '24px', padding: '20px', borderRadius: '16px', backgroundColor: '#ffffff', border: '1px solid #e1e8f0' }}>
                <h3>Inbox Labo</h3>
                <p style={{ marginTop: 0, color: '#475569' }}>Nouveaux ordres non traités : {labOrders.filter(o => (o.sentTo || []).includes('Pharmacie') && !(o.processedBy || []).includes('Pharmacie')).length}</p>
                {labOrders && labOrders.filter(o => (o.sentTo || []).includes('Pharmacie')).length > 0 ? (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {labOrders.filter(o => (o.sentTo || []).includes('Pharmacie')).map(o => (
                            <li key={o.id} style={{ padding: 12, borderBottom: '1px solid #eee' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                    <div>
                                        <div><strong>Ordre {o.id}</strong> - Patient: {o.patientId}</div>
                                        <div>Tests: {(o.tests || []).join(', ')}</div>
                                        <div style={{ color: '#334e68' }}>Statut : {(o.processedBy || []).includes('Pharmacie') ? 'Traité' : 'Nouveau'}</div>
                                        {o.results && <div style={{ marginTop: 6 }}><strong>Résultats labo :</strong> {o.results}</div>}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                                        <button onClick={() => setProcessingOrder(o)}>Traiter (ouvrir assistant)</button>
                                        <span style={{ color: '#6b7280' }}>{(o.sentTo || []).join(', ')}</span>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>Aucun ordre labo pour la pharmacie.</p>
                )}
            </section>

            <section style={{ marginBottom: '24px', padding: '20px', borderRadius: '16px', backgroundColor: '#ffffff', border: '1px solid #e1e8f0' }}>
                <h3>Ordonnances reçues</h3>
                {pharmacyOrders.length ? pharmacyOrders.slice().reverse().map(order => (
                    <div key={order.id} style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <strong>{order.patientName || 'Patient non renseigné'}</strong>
                        <div>{order.medications}</div>
                        <small>{order.date} · {order.status}</small>
                    </div>
                )) : <p>Aucune ordonnance reçue.</p>}
            </section>

            <section style={{ marginBottom: '24px', padding: '20px', borderRadius: '16px', backgroundColor: '#ffffff', border: '1px solid #e1e8f0' }}>
                <h3>Enregistrer une entrée / sortie</h3>
                <form onSubmit={submitTransaction} style={{ display: 'grid', gap: '14px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                        <label>
                            Article
                            <select value={transaction.itemId} onChange={(e) => handleTransactionChange('itemId', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }}>
                                <option value="">Sélectionnez un article</option>
                                {sortedStock.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </select>
                        </label>
                        <label>
                            Type de mouvement
                            <select value={transaction.type} onChange={(e) => handleTransactionChange('type', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }}>
                                <option value="sortie">Sortie</option>
                                <option value="entrée">Entrée</option>
                            </select>
                        </label>
                        <label>
                            Quantité
                            <input type="number" min="1" value={transaction.quantity} onChange={(e) => handleTransactionChange('quantity', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }} />
                        </label>
                    </div>
                    <div style={{ display: 'grid', gap: '14px' }}>
                        <label>
                            Signature / empreinte
                            <input type="text" value={transaction.signature} onChange={(e) => handleTransactionChange('signature', e.target.value)} placeholder="Signature ou empreinte" style={{ width: '100%', padding: '10px', marginTop: '6px' }} />
                        </label>
                        <label>
                            Notes
                            <textarea value={transaction.notes} onChange={(e) => handleTransactionChange('notes', e.target.value)} placeholder="Motif d'entrée / sortie" style={{ width: '100%', padding: '10px', marginTop: '6px' }} rows={3} />
                        </label>
                    </div>
                    <button type="submit" style={{ width: 'fit-content', padding: '12px 18px', borderRadius: '10px', border: 'none', backgroundColor: '#0d3b66', color: 'white', cursor: 'pointer' }}>Valider le mouvement</button>
                </form>
            </section>

            {processingOrder && (
                <section style={{ marginBottom: '24px', padding: '20px', borderRadius: '16px', backgroundColor: '#f0f9ff', border: '1px solid #dbeefb' }}>
                    <h3>Assistant de réservation pour Ordre {processingOrder.id} (Patient {processingOrder.patientId})</h3>
                    <div style={{ marginBottom: 8 }}>
                        <label>Rechercher un article: </label>
                        <input ref={searchRef} value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} onKeyDown={onSearchKeyDown} placeholder="Tapez pour rechercher..." style={{ width: '100%', padding: 8, marginTop: 6 }} />
                        {suggestions.length > 0 && (
                            <ul style={{ listStyle: 'none', margin: 6, padding: 6, border: '1px solid #e2e8f0', maxHeight: 180, overflow: 'auto' }}>
                                {suggestions.map((s, idx) => (
                                    <li
                                        key={s.id}
                                        style={{
                                            padding: 8,
                                            cursor: 'pointer',
                                            backgroundColor: idx === selectedSuggestionIndex ? '#eef6ff' : 'transparent',
                                            borderRadius: 8,
                                        }}
                                        onClick={() => addSelectedItem(s)}
                                        onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                                    >
                                        {s.name} — {s.quantity} en stock
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                <button type="button" onClick={() => setShowTemplates(!showTemplates)} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                                    {showTemplates ? 'Masquer les templates' : 'Afficher les templates'}
                                </button>
                                <button type="button" onClick={exportTemplates} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                                    Exporter les templates
                                </button>
                                <button type="button" onClick={importTemplates} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>
                                    Importer un template
                                </button>
                            </div>
                            {showTemplates && (
                                <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 12, backgroundColor: '#f8fafc' }}>
                                    <div style={{ display: 'grid', gap: 10 }}>
                                        <label>
                                            Nom du template
                                            <input value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} placeholder="Ex. Réserve urgente" style={{ width: '100%', padding: 8, marginTop: 6 }} />
                                        </label>
                                        <button type="button" onClick={() => {
                                            createTemplate(newTemplateName);
                                            setNewTemplateName('');
                                        }} style={{ padding: '10px 12px', borderRadius: 10, border: 'none', backgroundColor: '#0d3b66', color: '#fff' }}>
                                            Enregistrer le template
                                        </button>
                                    </div>
                                    <div style={{ marginTop: 14 }}>
                                        <h4 style={{ margin: '0 0 10px' }}>Templates enregistrés</h4>
                                        {templates.length === 0 ? (
                                            <p style={{ margin: 0, color: '#475569' }}>Aucun template enregistré.</p>
                                        ) : (
                                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                                                {templates.map(tpl => (
                                                    <li key={tpl.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: 10, border: '1px solid #dbeafe', borderRadius: 10, backgroundColor: '#ffffff' }}>
                                                        <span>{tpl.name}</span>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <button type="button" onClick={() => applyTemplate(tpl.id)} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', backgroundColor: '#ffffff' }}>Appliquer</button>
                                                            <button type="button" onClick={() => deleteTemplate(tpl.id)} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>Supprimer</button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <h4>Articles sélectionnés</h4>
                        {selectedItems.length === 0 ? <p>Aucun article sélectionné.</p> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr><th>Article</th><th>Quantité</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {selectedItems.map(si => (
                                        <tr key={si.itemId} style={{ borderTop: '1px solid #eee' }}>
                                            <td>{si.name}</td>
                                            <td><input type="number" min="1" value={si.qty} onChange={(e) => updateSelectedQty(si.itemId, e.target.value)} style={{ width: 80 }} /></td>
                                            <td><button onClick={() => removeSelected(si.itemId)}>Retirer</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <div style={{ marginTop: 12 }}>
                            <button onClick={() => reserveForOrder(processingOrder.id)}>Réserver et notifier</button>
                            <button onClick={() => { setProcessingOrder(null); setSelectedItems([]); }} style={{ marginLeft: 8 }}>Annuler</button>
                        </div>
                    </div>
                </section>
            )}

            <section style={{ padding: '20px', borderRadius: '16px', backgroundColor: '#f8f9fb', border: '1px solid #dde4ed' }}>
                <h3>Dernières transactions</h3>
                {stockTransactions.length > 0 ? (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '12px' }}>
                        {stockTransactions.slice(0, 8).map(tx => (
                            <li key={tx.id} style={{ padding: '14px', borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #dfe6ee' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                    <span><strong>{tx.type === 'entrée' ? 'Entrée' : 'Sortie'}</strong> - {tx.itemName}</span>
                                    <span>{tx.date}</span>
                                </div>
                                <p style={{ margin: '8px 0 0' }}>{tx.quantity} unité(s) par {tx.performedBy}</p>
                                <p style={{ margin: '4px 0 0', color: '#475569' }}>Signature: {tx.signature || '—'}</p>
                                {tx.notes && <p style={{ margin: '4px 0 0', color: '#475569' }}>Notes: {tx.notes}</p>}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>Aucune transaction enregistrée pour le moment.</p>
                )}
            </section>

            <section style={{ marginTop: '24px', padding: '20px', borderRadius: '16px', backgroundColor: '#ffffff', border: '1px solid #e1e8f0' }}>
                <h3>Ajouter un article au stock</h3>
                <form onSubmit={submitNewItem} style={{ display: 'grid', gap: '14px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                        <label>
                            Nom de l'article
                            <input type="text" value={newItem.name} onChange={(e) => handleNewItemChange('name', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }} />
                        </label>
                        <label>
                            Description
                            <input type="text" value={newItem.description} onChange={(e) => handleNewItemChange('description', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }} />
                        </label>
                        <label>
                            Quantité initiale
                            <input type="number" min="0" value={newItem.quantity} onChange={(e) => handleNewItemChange('quantity', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }} />
                        </label>
                        <label>
                            Capacité maximale
                            <input type="number" min="1" value={newItem.capacity} onChange={(e) => handleNewItemChange('capacity', e.target.value)} style={{ width: '100%', padding: '10px', marginTop: '6px' }} />
                        </label>
                    </div>
                    <button type="submit" style={{ width: 'fit-content', padding: '12px 18px', borderRadius: '10px', border: 'none', backgroundColor: '#0d3b66', color: 'white', cursor: 'pointer' }}>Ajouter l'article</button>
                </form>
            </section>
        </div>
    );
};

export default PharmacyView;
